

import React, { useState, useMemo } from 'react';
import type { Athlete, Course } from '../types';
import { calculateLapsForAthlete, Lap, OffsetAdjustment, RaceIncident } from '../services/raceAnalysis';
import { AlertIcon } from './icons/PlaybackIcons';

const formatLapTime = (ms: number) => {
  if (ms === 0) return '—';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

interface LapDataProps {
  athletes: Athlete[];
  course: Course;
  dynamicOffsets: Map<string, OffsetAdjustment[]>;
  incidents?: Map<string, RaceIncident[]>;
  currentUserId?: string;
}

interface DisplayLap extends Lap {
    isPlaceholder?: boolean;
    isPartial?: boolean;
    isReintegration?: boolean;
    hasIncident?: boolean;
    incidentType?: string;
    incidentDescription?: string;
    matchCount?: number;
}

const LapData: React.FC<LapDataProps> = ({ athletes, course, dynamicOffsets, incidents, currentUserId }) => {
    const [selectedAthleteId, setSelectedAthleteId] = useState<string | null>(athletes[0]?.id || null);

    const laps = useMemo<DisplayLap[]>(() => {
        if (!selectedAthleteId) return [];
        const athlete = athletes.find(a => a.id === selectedAthleteId);
        if (!athlete || !athlete.track) return [];

        const rawLaps = calculateLapsForAthlete(athlete.track, course.start);
        const adjustments = dynamicOffsets.get(selectedAthleteId) || [];
        // Safe access for incidents
        const athleteIncidents = incidents ? (incidents.get(selectedAthleteId) || []) : [];
        
        const displayLaps: DisplayLap[] = [];
        let currentTrackTime = athlete.track[0].timestamp;
        let lastVirtualLapNumber = 0;

        for (let i = 0; i < rawLaps.length; i++) {
            const lap = rawLaps[i];
            const lapEndTime = currentTrackTime + lap.lapTime;
            
            // Check for incidents in this lap window
            const lapIncident = athleteIncidents.find(inc => 
                inc.timestamp >= currentTrackTime && inc.timestamp <= lapEndTime
            );

            // Check for matches in this lap window (only for logged-in user)
            const isCurrentUser = currentUserId && (athlete.userId === currentUserId || athlete.id === currentUserId);
            const lapMatches = isCurrentUser ? (athlete.matchEvents || []).filter(m => 
                m.time >= currentTrackTime && m.time <= lapEndTime
            ) : [];
            const matchCount = lapMatches.length;

            // Find active offset at the start of this lap
            let activeOffset = 0;
            for (let j = adjustments.length - 1; j >= 0; j--) {
                if (adjustments[j].timestamp <= currentTrackTime) {
                    activeOffset = adjustments[j].lapOffset;
                    break;
                }
            }

            const virtualLapNumber = lap.lapNumber + activeOffset;
            
            // Fill gaps (e.g. if we jumped from 1 to 5, insert 2,3,4)
            for (let filler = lastVirtualLapNumber + 1; filler < virtualLapNumber; filler++) {
                displayLaps.push({
                    lapNumber: filler,
                    lapTime: 0,
                    avgSpeed: 0,
                    avgPower: null,
                    avgHeartRate: null,
                    isPlaceholder: true
                });
            }

            displayLaps.push({
                ...lap,
                lapNumber: virtualLapNumber,
                isReintegration: (virtualLapNumber - lastVirtualLapNumber > 1) && i > 0,
                hasIncident: !!lapIncident,
                incidentType: lapIncident?.type,
                incidentDescription: lapIncident?.description,
                matchCount
            });
            
            lastVirtualLapNumber = virtualLapNumber;
            currentTrackTime += lap.lapTime;
        }

        return displayLaps;
    }, [selectedAthleteId, athletes, course.start, dynamicOffsets, incidents]);
    
    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedAthleteId(e.target.value);
    };

    return (
        <div className="w-full flex flex-col text-sm">
            <div className="flex-shrink-0 mb-2">
                <select 
                    value={selectedAthleteId || ''} 
                    onChange={handleSelectChange}
                    className="w-full bg-gray-700 text-white rounded-md px-3 py-1.5 text-sm appearance-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    aria-label="Select athlete for lap data"
                >
                    {athletes.map(athlete => (
                        <option key={athlete.id} value={athlete.id}>{athlete.name}</option>
                    ))}
                </select>
            </div>
            <div className="overflow-y-auto max-h-[300px]">
                <table className="w-full text-left table-fixed">
                    <thead className="sticky top-0 bg-gray-800 z-10 shadow-sm">
                        <tr className="text-xs text-gray-400 uppercase">
                            <th className="p-2 w-[25%]">Lap</th>
                            <th className="p-2 w-[20%]">Time</th>
                            <th className="p-2 w-[20%]">Avg. Speed</th>
                            <th className="p-2 w-[15%]">Power</th>
                            <th className="p-2 w-[15%]">HR</th>
                        </tr>
                    </thead>
                    <tbody>
                        {laps.length > 0 ? laps.map((lap: DisplayLap) => (
                            <tr key={lap.lapNumber} className={`border-b border-gray-700 hover:bg-gray-700/50 ${lap.isPlaceholder ? 'opacity-50 bg-gray-800/50' : ''}`}>
                                <td className="p-2 font-semibold text-gray-300">
                                        <div className="flex items-center gap-2">
                                            <span>{lap.lapNumber}</span>
                                            {lap.isReintegration && <span className="text-[10px] text-cyan-400" title="Re-integrated (Offset Applied)">⚡</span>}
                                            {lap.hasIncident && (
                                                <div className="group relative cursor-help" title={`${lap.incidentType}: ${lap.incidentDescription}`}>
                                                    <AlertIcon className="w-5 h-5 text-red-500 animate-pulse" />
                                                </div>
                                            )}
                                            {lap.matchCount && lap.matchCount > 0 ? (
                                                <span className="text-sm cursor-help" title={`${lap.matchCount} Matches Burned`}>
                                                    {lap.matchCount > 1 ? `🔥 (${lap.matchCount}x)` : '🔥'}
                                                </span>
                                            ) : null}
                                        </div>
                                </td>
                                {lap.isPlaceholder ? (
                                    <td colSpan={4} className="p-2 text-center text-gray-600 italic text-xs">
                                        Not Recorded / Pit
                                    </td>
                                ) : (
                                    <>
                                        <td className="p-2 font-mono">{formatLapTime(lap.lapTime)}</td>
                                        <td className="p-2">{(lap.avgSpeed * 0.621371).toFixed(1)}</td>
                                        <td className="p-2">{lap.avgPower !== null ? Math.round(lap.avgPower) : '—'}</td>
                                        <td className="p-2">{lap.avgHeartRate !== null ? Math.round(lap.avgHeartRate) : '—'}</td>
                                    </>
                                )}
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={5} className="p-4 text-center text-gray-500">
                                    {selectedAthleteId ? 'No lap data available for this athlete.' : 'Select an athlete to view lap data.'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
             <div className="mt-2 px-2 flex flex-wrap gap-2">
                {laps.some(l => l.isPlaceholder) && (
                    <div className="text-[10px] text-gray-500">
                        * Grey rows: Missed laps (Late Start / Pit)
                    </div>
                )}
                {laps.some(l => l.isReintegration) && (
                    <div className="text-[10px] text-cyan-400">
                        ⚡ Re-integration detected
                    </div>
                )}
                 {laps.some(l => l.hasIncident) && (
                    <div className="text-[10px] text-red-400 flex items-center gap-1 font-bold">
                        <AlertIcon className="w-3 h-3" /> Incident Detected
                    </div>
                )}
                 {laps.some(l => l.matchCount && l.matchCount > 0) && (
                    <div className="text-[10px] text-orange-400 flex items-center gap-1 font-bold">
                        🔥 Match Burned
                    </div>
                )}
            </div>
        </div>
    );
};

export default LapData;
